import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, Card, CardContent } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getIconByName } from '../../../utils/iconMap';
import { portalContentMaxWidthSx, portalPageHorizontalPaddingSx } from '../../../theme/portalLayout';
function accentColors(theme, accent) {
    switch (accent) {
        case 'green':
            return { tileBg: alpha('#2e7d32', 0.12), iconColor: '#2e7d32' };
        case 'amber':
            /* Orange-tinted well (document card) — matches creative mockup */
            return {
                tileBg: alpha('#e65100', 0.14),
                iconColor: '#e65100',
            };
        case 'gold':
        default:
            return {
                tileBg: alpha(theme.palette.secondary.main, 0.14),
                iconColor: theme.palette.secondary.main,
            };
    }
}
const GetStartedSection = ({ features = [], badgeLabel, badgeIcon = 'AccessTime', heading, subheading, items = [], overlapSpacing = 5, afterHeroTrustBarSpacing = 0, }) => {
    const theme = useTheme();
    const BadgeIcon = getIconByName(badgeIcon);
    const trustBarClearance = afterHeroTrustBarSpacing > 0 ? theme.spacing(afterHeroTrustBarSpacing) : 0;
    const defaultPt = features.length > 0
        ? 0
        : trustBarClearance
            ? {
                xs: `calc(${theme.spacing(2)} + ${trustBarClearance})`,
                md: `calc(${theme.spacing(3)} + ${trustBarClearance})`,
            }
            : { xs: 2, md: 3 };
    return (_jsxs(Box, { id: "get-started", component: "section", sx: {
            position: 'relative',
            zIndex: 2,
            bgcolor: '#ffffff',
            pt: defaultPt,
            pb: { xs: 5, md: 7 },
        }, children: [features.length > 0 && (_jsx(Box, { sx: {
                    ...portalContentMaxWidthSx,
                    ...portalPageHorizontalPaddingSx,
                    mt: (themeArg) => themeArg.spacing(-overlapSpacing),
                    mb: { xs: 1.5, md: 2 },
                }, children: _jsx(Box, { sx: {
                        bgcolor: '#fff',
                        borderRadius: 2.5,
                        py: { xs: 2, md: 2.5 },
                        px: { xs: 2, md: 3 },
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-around',
                        alignItems: 'flex-start',
                        gap: { xs: 2.5, md: 3 },
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.08),
                        boxShadow: '0 12px 40px rgba(0, 40, 85, 0.12)',
                    }, children: features.map((f, i) => {
                        const Icon = getIconByName(f.icon);
                        return (_jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                maxWidth: 280,
                                flex: { xs: '1 1 100%', sm: '1 1 40%', md: '0 1 auto' },
                            }, children: [_jsx(Icon, { sx: { color: 'primary.main', fontSize: 32, mt: 0.25, flexShrink: 0 } }), _jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700, color: 'primary.dark' }, children: f.title }), _jsx(Typography, { variant: "body2", sx: { color: 'text.secondary', lineHeight: 1.45 }, children: f.subtitle })] })] }, i));
                    }) }) })), _jsxs(Box, { sx: {
                    ...portalContentMaxWidthSx,
                    ...portalPageHorizontalPaddingSx,
                    textAlign: 'center',
                }, children: [badgeLabel ? (_jsxs(Box, { sx: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 2,
                            py: 0.75,
                            mb: 2,
                            borderRadius: 999,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.12),
                        }, children: [_jsx(BadgeIcon, { sx: { fontSize: 18, color: 'primary.main' } }), _jsx(Typography, { variant: "caption", sx: { fontWeight: 700, color: 'primary.main', letterSpacing: 0.4 }, children: badgeLabel })] })) : null, _jsx(Typography, { variant: "h3", component: "h2", sx: {
                            fontWeight: 800,
                            color: 'primary.dark',
                            mb: 1.5,
                            fontSize: { xs: '1.65rem', md: '2.125rem' },
                        }, children: heading }), subheading && (_jsx(Typography, { variant: "body1", sx: {
                            color: 'text.secondary',
                            maxWidth: 720,
                            mx: 'auto',
                            mb: { xs: 3, md: 4 },
                            lineHeight: 1.65,
                        }, children: subheading })), _jsx(Box, { sx: {
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                            gap: { xs: 2, md: 2.5 },
                            textAlign: 'center',
                        }, children: items.map((item, index) => {
                            const Icon = getIconByName(item.icon);
                            const { tileBg, iconColor } = accentColors(theme, item.accent);
                            return (_jsx(Card, { elevation: 0, sx: {
                                    height: '100%',
                                    borderRadius: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: '0 2px 12px rgba(0,40,85,0.06)',
                                }, children: _jsxs(CardContent, { sx: { px: 2.5, py: 3 }, children: [_jsx(Box, { sx: {
                                                width: 56,
                                                height: 56,
                                                borderRadius: '50%',
                                                bgcolor: tileBg,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                mb: 2,
                                            }, children: _jsx(Icon, { sx: { fontSize: 26, color: iconColor } }) }), _jsx(Typography, { variant: "subtitle1", sx: {
                                                fontFamily: (t) => t.typography.h5.fontFamily,
                                                fontWeight: 600,
                                                color: 'primary.dark',
                                                mb: 0.75,
                                            }, children: item.title }), item.description && (_jsx(Typography, { variant: "body2", sx: {
                                                fontFamily: (t) => t.typography.body2.fontFamily,
                                                color: 'text.secondary',
                                                lineHeight: 1.5,
                                            }, children: item.description }))] }) }, index));
                        }) })] })] }));
};
export default GetStartedSection;
